import { FC, ReactNode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiMaximize2, FiChevronDown } from 'react-icons/fi';

interface MobileChartOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * A mobile-only chart overlay that displays a chart in a full-screen overlay
 * Only appears on mobile devices (screens smaller than 768px)
 */
const MobileChartOverlay: FC<MobileChartOverlayProps> = ({
  isOpen,
  onClose,
  title,
  children
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Check if the device is mobile on mount and when window is resized
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up event listener
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Prevent body scrolling when overlay is open
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);
  
  // Close on escape key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  
  // Toggle expanded state
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Only render on mobile and when open
  if (!isOpen || !isMobile) return null;
  
  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex flex-col bg-gradient-to-br from-slate-900 via-brand-blue/50 to-brand-purple/50 bg-fixed overflow-hidden"
      onClick={(e) => {
        // Only close if the click is on the wrapper, not on the content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Header with glassmorphic effect */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <span>{title}</span>
          <button 
            onClick={toggleExpanded}
            className="ml-2 text-white/60 hover:text-white p-1"
          >
            <FiChevronDown 
              size={18} 
              className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
            />
          </button>
        </h2>
        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            aria-label="Toggle fullscreen"
          >
            <FiMaximize2 size={18} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </div>
      </div>
      
      {/* Chart content with animation */}
      <div 
        className={`flex-1 p-0 overflow-auto transform transition-all duration-300 ${
          isExpanded ? 'max-h-full' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4">
          {children}
        </div>
      </div>
      
      {/* Bottom handle for collapsible UI - only shown when collapsed */}
      {!isExpanded && (
        <div 
          className="absolute bottom-0 left-0 right-0 p-2 flex justify-center text-white/50"
          onClick={toggleExpanded}
        >
          <div className="w-12 h-1 bg-white/20 rounded-full"></div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default MobileChartOverlay; 